import { useState } from 'react'
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronDown, ChevronUp, Check, X, Undo2 } from 'lucide-react'
import type { IntentMergeGroup } from '@/types/reconciliation'

interface MergeGroupCardProps {
  group: IntentMergeGroup
  onApprove: (groupId: string) => void
  onReject: (groupId: string) => void
  finalized: boolean
}

function getCardBorder(confidence: number, userApproved: boolean | null): string {
  if (userApproved === true) return 'border-green-500 bg-green-50/60'
  if (userApproved === false) return 'border-red-400 bg-red-50/40'
  if (confidence >= 0.90) return 'border-green-300 bg-green-50/30'
  return 'border-yellow-300 bg-yellow-50/30'
}

export function MergeGroupCard({ group, onApprove, onReject, finalized }: MergeGroupCardProps) {
  const [expanded, setExpanded] = useState(false)
  const merged = group.merged_intent_json
  const tagsToMerge = merged?.tags_to_merge ?? []
  const entityTypesToMerge = merged?.entity_types_to_merge ?? []

  return (
    <Card className={`transition-colors ${getCardBorder(group.confidence, group.user_approved)}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant={group.confidence >= 0.90 ? 'default' : 'secondary'}
              className={
                group.confidence >= 0.90
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-yellow-500 hover:bg-yellow-600 text-white'
              }
            >
              {group.confidence >= 0.90 ? 'High Confidence' : 'Suggested Merge'}
              {' '}({(group.confidence * 100).toFixed(0)}%)
            </Badge>

            {group.user_approved === true && (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <Check className="w-3 h-3 mr-1" /> Approved
              </Badge>
            )}
            {group.user_approved === false && (
              <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">
                <X className="w-3 h-3 mr-1" /> Rejected
              </Badge>
            )}
            {group.user_approved === null && group.confidence >= 0.90 && (
              <Badge className="bg-blue-100 text-blue-800 border-blue-300">Auto-approved</Badge>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <p className="font-semibold text-sm">
          Merge {group.source_intents.length} intents &rarr; &ldquo;{merged?.name}&rdquo;
        </p>

        <div className="mt-2">
          <p className="text-xs text-muted-foreground mb-1">Source intents:</p>
          <ul className="list-disc list-inside text-sm space-y-0.5">
            {group.source_intents.map((intent) => (
              <li key={intent.id} className="text-gray-700">
                &ldquo;{intent.name}&rdquo;
                <span className="text-muted-foreground ml-1">({intent.document_count} docs)</span>
              </li>
            ))}
          </ul>
        </div>

        {expanded && (
          <div className="mt-4 space-y-3 border-t pt-3">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Merged Description</h4>
              <p className="text-sm mt-1">{merged?.description}</p>
            </div>

            {tagsToMerge.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tag Merges</h4>
                <ul className="text-sm mt-1 space-y-0.5">
                  {tagsToMerge.map((tag, idx) => (
                    <li key={idx}>
                      <span className="font-medium">{tag.keep}</span>
                      {tag.aliases.length > 0 && (
                        <span className="text-muted-foreground">
                          {' '}&larr; {tag.aliases.join(', ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {entityTypesToMerge.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Entity Type Merges</h4>
                <ul className="text-sm mt-1 space-y-0.5">
                  {entityTypesToMerge.map((et, idx) => (
                    <li key={idx}>
                      <span className="font-medium">{et.keep}</span>
                      {et.aliases.length > 0 && (
                        <span className="text-muted-foreground">
                          {' '}&larr; {et.aliases.join(', ')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Why merge these?</h4>
              <p className="text-sm mt-1 text-muted-foreground">{group.reasoning}</p>
            </div>
          </div>
        )}
      </CardContent>

      {!finalized && (
        <CardFooter className="pt-0 flex gap-2">
          {group.user_approved === true ? (
            <Button variant="outline" size="sm" onClick={() => onReject(group.id)}>
              <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo &mdash; Keep Separate
            </Button>
          ) : group.user_approved === false ? (
            <Button variant="outline" size="sm" onClick={() => onApprove(group.id)}>
              <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo &mdash; Approve Merge
            </Button>
          ) : (
            <>
              <Button size="sm" onClick={() => onApprove(group.id)} className="bg-green-600 hover:bg-green-700">
                <Check className="w-3.5 h-3.5 mr-1" /> Approve Merge
              </Button>
              <Button variant="outline" size="sm" onClick={() => onReject(group.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                <X className="w-3.5 h-3.5 mr-1" /> Keep Separate
              </Button>
            </>
          )}
        </CardFooter>
      )}

      {finalized && group.decided_at && (
        <CardFooter className="pt-0">
          <p className="text-xs text-muted-foreground">
            Decision made on {new Date(group.decided_at).toLocaleDateString()}
          </p>
        </CardFooter>
      )}
    </Card>
  )
}
